import React, { useEffect, useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import type { DocEntry } from 'docs'

import {
  ANCHOR_OFFSET,
  DOCS_HEADER_HEIGHT,
  DOCS_STICKY_TOP,
  DOCS_TOC_TOP_OFFSET,
} from './constants'

interface TocHeading {
  id: string
  text: string
  /** 2 sits at the rail; deeper values indent under it. */
  depth: number
}

interface Props {
  /**
   * The headings to list. Taken as a plain array rather than a `DocEntry` so the glossary can
   * reuse this rail for its kind sections — the rail only ever needed ids, text and depth, and
   * requiring a whole guide meant a second, drifting copy for anything that is not one.
   */
  headings: readonly TocHeading[]
  /** Changes when the underlying page does, so the observer re-attaches. */
  scrollKey: string
  onSelect: (id: string) => void
}

/**
 * Right column: this page's own table of contents, built from the manifest.
 *
 * It renders before the prose does, because the headings were extracted at publish time
 * rather than read out of the DOM after mount. That is the same property that makes the
 * anchors linkable from outside the page.
 *
 * The active row is tracked by observing the rendered headings. If the prose chunk has
 * not arrived yet there is simply nothing to observe and no row is highlighted; the
 * observer re-attaches when the headings appear.
 */
export const DocToc = ({ headings, scrollKey, onSelect }: Props) => {
  const theme = useTheme()
  const [activeId, setActiveId] = useState<string | undefined>()

  useEffect(() => {
    setActiveId(undefined)
    if (!headings.length) return

    let frame = 0
    let observer: IntersectionObserver | undefined

    // The headings belong to a lazily-loaded chunk, so they may not be in the DOM on
    // the first pass. Retry on animation frames rather than a timeout — it costs
    // nothing once the elements exist, and avoids guessing how long the chunk takes.
    const attach = () => {
      const els = headings
        .map(h => document.getElementById(h.id))
        .filter((el): el is HTMLElement => !!el)

      if (!els.length) {
        frame = requestAnimationFrame(attach)
        return
      }

      observer = new IntersectionObserver(
        entries => {
          const visible = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
          if (visible[0]) setActiveId(visible[0].target.id)
        },
        {
          // Treat "active" as "has reached the line the anchor scroll parks headings
          // on", so the highlighted row matches the section at the top of the view
          // rather than whichever heading happens to be mid-screen.
          rootMargin: `-${ANCHOR_OFFSET}px 0px -70% 0px`,
          threshold: 0,
        },
      )
      els.forEach(el => observer?.observe(el))
    }

    attach()
    return () => {
      cancelAnimationFrame(frame)
      observer?.disconnect()
    }
  }, [scrollKey, headings])

  if (!headings.length) return null

  return (
    <Box
      sx={{
        position: 'sticky',
        top: `${DOCS_STICKY_TOP + DOCS_TOC_TOP_OFFSET}px`,
        mt: `${DOCS_TOC_TOP_OFFSET}px`,
      }}
    >
      {/* Same header band as the search field and the document title, so all three columns
          start on one line (DOCS_HEADER_HEIGHT). */}
      <Box
        sx={{
          height: DOCS_HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body4"
          color="text.primary"
          sx={{
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            fontWeight: 700,
          }}
        >
          On this page
        </Typography>
      </Box>
      {/* The rows carry their own vertical padding rather than being spaced apart, so the
          hit target is the full row height and consecutive entries read as one list. At
          spacing={2} with 14px text the gaps were wider than the text was tall, which is
          what made a four-item contents look like four unrelated links. */}
      <Stack
        component="nav"
        aria-label="On this page"
        sx={{ borderLeft: `1px solid ${theme.palette.grey[100]}` }}
      >
        {headings.map(h => {
          const active = h.id === activeId
          return (
            <Typography
              key={h.id}
              component="a"
              href={`#${h.id}`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                onSelect(h.id)
              }}
              variant="body3"
              sx={{
                display: 'block',
                textDecoration: 'none',
                cursor: 'pointer',
                lineHeight: 1.35,
                py: 1,
                // Depth 2 sits at the rail; deeper headings indent under it.
                pl: h.depth <= 2 ? 3 : 3 + (h.depth - 2) * 2,
                ml: '-1px',
                borderLeft: `2px solid ${active ? theme.palette.brand[600] : 'transparent'}`,
                // Neutral text, brand RAIL. Colouring the whole list brand made four links
                // in the margin louder than the prose they point into, and it spent the
                // accent on a passive index — the one place on the page that is pure
                // navigation. The rail plus weight marks the current row, which is enough
                // to find it without tinting the other three.
                color: active ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: active ? 600 : 400,
                transition: 'color 120ms ease',
                '&:hover': { color: theme.palette.text.primary },
              }}
            >
              {h.text}
            </Typography>
          )
        })}
      </Stack>
    </Box>
  )
}
